import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config';
import { getDeviceId } from '../utils/deviceId';

const AUTH_API_URL = `${API_BASE_URL}/api/v1/auth`;

interface DecodedToken {
    id: string;
    company: string | null;
    role: string;
    name: string;
    username: string;
    iat: number;
    exp: number;
}

export interface RegisterCompanyData {
    companyName: string;
    companyDomain: string;
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    telephone?: string;
    description?: string;
    language?: string; // Language used during registration (e.g., 'zh' or 'en')
}

export interface LoginData {
    companyId?: string;
    username: string;
    password: string;
}

export interface RegisteredCompanyOption {
    companyId: string;
    name: string;
}

const rememberedLoginCompanyKey = 'rememberedLoginCompany';

export interface LoginResponse {
    success: boolean;
    token?: string;
    passwordResetRequired?: boolean;
    passwordResetToken?: string;
    resetToken?: string;
    message?: string;
    user?: {
        id: string;
        username: string;
        name: string;
        role: string;
        company?: string;
        defaultChatType?: 'rag' | 'gpt';
    };
}

export interface SetNewPasswordData {
    passwordResetToken?: string;
    username?: string;
    newPassword: string;
    confirmPassword: string;
}

export interface ChangePasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export const authService = {
    async registerCompany(data: RegisterCompanyData) {
        const response = await axios.post(`${AUTH_API_URL}/register-company`, { ...data, deviceId: getDeviceId() });
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
        }
        return response.data;
    },

    async login(data: LoginData): Promise<LoginResponse> {
        const response = await axios.post(`${AUTH_API_URL}/login`, { ...data, deviceId: getDeviceId() });
        
        // Handle password reset required (status 202)
        if (response.status === 202 && response.data.passwordResetRequired) {
            return response.data;
        }
        
        // Normal login success
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            // Store user info including default chat type
            if (response.data.user) {
                localStorage.setItem('userInfo', JSON.stringify(response.data.user));
            }
        }
        return response.data;
    },

    async setNewPassword(data: SetNewPasswordData): Promise<LoginResponse> {
        const response = await axios.post(`${AUTH_API_URL}/set-new-password`, { ...data, deviceId: getDeviceId() });
        if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            // Store user info including default chat type
            if (response.data.user) {
                localStorage.setItem('userInfo', JSON.stringify(response.data.user));
            }
        }
        return response.data;
    },

    /** Change the authenticated user's password after verifying the current password. */
    async changePassword(data: ChangePasswordData): Promise<{ success: boolean; message?: string }> {
        const token = localStorage.getItem('token');
        const response = await axios.put(`${AUTH_API_URL}/change-password`, data, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        return response.data;
    },

    /** Search approved companies for the public tenant selector on the login page. */
    async searchRegisteredCompanies(query: string, limit = 8): Promise<RegisteredCompanyOption[]> {
        const normalizedQuery = query.trim();
        if (normalizedQuery.length < 2) {
            return [];
        }

        const response = await axios.get(`${AUTH_API_URL}/companies/search`, {
            params: { q: normalizedQuery, limit }
        });
        const items = Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data?.companies)
                ? response.data.companies
                : [];

        return items
            .map((company: any) => ({
                companyId: String(company.companyId || company.id || company._id || ''),
                name: String(company.name || company.companyName || '')
            }))
            .filter((company: RegisteredCompanyOption) => (
                company.companyId && company.companyId !== 'M000000' && company.name
            ))
            .slice(0, limit);
    },

    /** Keep the last tenant selection in this browser without exposing its ID in the UI. */
    rememberLoginCompany(company: RegisteredCompanyOption) {
        localStorage.setItem(rememberedLoginCompanyKey, JSON.stringify(company));
    },

    getRememberedLoginCompany(): RegisteredCompanyOption | null {
        try {
            const stored = localStorage.getItem(rememberedLoginCompanyKey);
            if (!stored) return null;

            const company = JSON.parse(stored);
            if (
                typeof company?.companyId !== 'string'
                || company.companyId === 'M000000'
                || typeof company?.name !== 'string'
                || !company.name.trim()
            ) {
                localStorage.removeItem(rememberedLoginCompanyKey);
                return null;
            }

            return { companyId: company.companyId, name: company.name };
        } catch {
            localStorage.removeItem(rememberedLoginCompanyKey);
            return null;
        }
    },

    clearRememberedLoginCompany() {
        localStorage.removeItem(rememberedLoginCompanyKey);
    },

    logout() {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
    },

    getToken() {
        return localStorage.getItem('token');
    },

    decodeToken(): DecodedToken | null {
        const token = this.getToken();
        if (!token) return null;
        try {
            return jwtDecode<DecodedToken>(token);
        } catch (e) {
            console.error("Failed to decode token:", e);
            return null;
        }
    },

    getUserInfo() {
        const userInfo = localStorage.getItem('userInfo');
        if (!userInfo) return null;
        try {
            return JSON.parse(userInfo);
        } catch (e) {
            console.error("Failed to parse user info:", e);
            return null;
        }
    },

    getDefaultChatType(): 'rag' | 'gpt' {
        const userInfo = this.getUserInfo();
        return userInfo?.defaultChatType || 'gpt';
    },

    /**
     * Fetch language setting from database (company settings or system settings)
     * This should be called after login to get the user's language preference
     * Uses the /language endpoint which is accessible to all authenticated users (including members)
     */
    async fetchLanguageFromDatabase(): Promise<string | null> {
        try {
            const token = this.getToken();
            if (!token) {
                console.log('[Auth] No token found, cannot fetch language from database');
                return null;
            }

            // Fetch language setting from server (accessible to all authenticated users)
            console.log('[Auth] Fetching language from endpoint: /api/v1/system-settings/language');
            const response = await axios.get(`${API_BASE_URL}/api/v1/system-settings/language`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('[Auth] Language API response:', {
                success: response.data.success,
                data: response.data.data,
                status: response.status
            });

            if (response.data.success && response.data.data) {
                const language = response.data.data.defaultLanguage;
                console.log('[Auth] Language from response.data.data.defaultLanguage:', language, 'type:', typeof language);
                if (language === 'en' || language === 'zh') {
                    console.log('[Auth] ✅ Valid language fetched from database:', language);
                    // Save to localStorage for faster subsequent loads
                    localStorage.setItem('userLanguage', language);
                    console.log('[Auth] ✅ Language saved to localStorage:', localStorage.getItem('userLanguage'));
                    return language;
                } else {
                    console.warn('[Auth] ⚠️ Invalid language value from database:', language, 'type:', typeof language);
                }
            } else {
                console.warn('[Auth] ⚠️ Invalid response structure:', response.data);
            }

            console.log('[Auth] ❌ No valid language found in database response');
            return null;
        } catch (error: any) {
            console.error('[Auth] Error fetching language from database:', error);
            if (error.response) {
                console.error('[Auth] Response status:', error.response.status);
                console.error('[Auth] Response data:', error.response.data);
            }
            return null;
        }
    }
};
